import React__default, { useRef, useCallback, useEffect } from 'react';
import { useSlate, ReactEditor, useFocused, Editable as Editable$1 } from 'slate-react';
import { Transforms, Editor } from 'slate';
import { isAndroidUserAgent } from '../../../../../utilities/user-agent.mjs';
import { useCombineDecorators } from '../../hooks/useCombineDecorators.mjs';
import { useElementRenderers } from '../../hooks/useElementRenderers.mjs';
import { useLeafRenderers } from '../../hooks/useLeafRenderers.mjs';
import { combineHandlers } from '../../utilities/handlers/combineHandlers.mjs';
import { getPluginHandlers } from '../../utilities/handlers/getPluginHandlers.mjs';

/* eslint-disable react-hooks/exhaustive-deps */
/**
 * {@link Editable} with plugins support.
 */
const Editable = ({
  plugins = [],
  onDOMBeforeInput,
  onKeyDown,
  ...props
}) => {
  const editor = useSlate();
  const timeout = useRef(null);
  const handleAndroidInput = useCallback(_event => {
    // Force Slate to reconcile its state with the DOM on user input.
    // This is necessary because the latest versions of Slate no longer
    // reconciles the DOM after each change, but instead wait for the composition
    // end event to be fired, which may never happen on Android until the keyboard
    // is dismissed or the input is blurred.
    queueMicrotask(() => {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = null;
      }
      const pendingDiffs = ReactEditor.androidPendingDiffs(editor);
      const immediate = pendingDiffs === null || pendingDiffs === void 0 ? void 0 : pendingDiffs.some(({
        diff
      }) => diff.text.includes(' '));
      if (immediate) {
        // Reconcile the editor state immediately
        ReactEditor.androidScheduleFlush(editor);
      } else {
        // Reconcile editor state after 1 second of inactivity
        timeout.current = setTimeout(() => ReactEditor.androidScheduleFlush(editor), 1000);
      }
    });
  }, [editor]);
  const {
    autoFocus
  } = props;
  useEffect(() => {
    if (autoFocus && !ReactEditor.isFocused(editor)) {
      ReactEditor.focus(editor);
    }
  }, [autoFocus]);
  const isFocused = useFocused();
  const focusedWithoutSelection = isFocused && editor.selection === null;

  // bugfix for Slate not automatically selecting the end of the document when autofocussed
  // see https://github.com/ianstormtaylor/slate/issues/5213 for more context
  useEffect(() => {
    if (focusedWithoutSelection) {
      Transforms.select(editor, {
        anchor: Editor.end(editor, []),
        focus: Editor.end(editor, [])
      });
    }
  }, [focusedWithoutSelection]);
  return /*#__PURE__*/React__default.createElement(Editable$1, Object.assign({
    decorate: useCombineDecorators(editor, plugins),
    renderElement: useElementRenderers(plugins),
    renderLeaf: useLeafRenderers(plugins),
    onDOMBeforeInput: useCallback(combineHandlers(editor, ...getPluginHandlers(plugins, 'onDOMBeforeInput'), isAndroidUserAgent() ? handleAndroidInput : undefined, onDOMBeforeInput), [editor, plugins, onDOMBeforeInput]),
    onKeyDown: useCallback(combineHandlers(editor, ...getPluginHandlers(plugins, 'onKeyDown'), onKeyDown), [editor, plugins, onKeyDown])
    // Disable auto-scrolling functionality because the browser already
    // handles this well, and Slate's scrolling functionality is broken in
    // recent versions: https://github.com/ianstormtaylor/slate/issues/5088
    ,
    scrollSelectionIntoView: noop
  }, props));
};
function noop() {}

export { Editable };
